import { ImgHTMLAttributes } from "react";

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "loading"> & {
  /**
   * Path to the primary image (jpg/png). If a sibling .webp file exists,
   * pass it as `webpSrc` and it will be used preferentially via <picture>.
   * If `webpSrc` is omitted and `src` ends with .jpg/.jpeg/.png, a sibling
   * .webp will be requested automatically (with graceful fallback).
   */
  src: string;
  webpSrc?: string;
  alt: string;
  width: number;
  height: number;
  /** true for above-the-fold imagery; everything else lazy-loads. */
  priority?: boolean;
};

const deriveWebp = (src: string): string | undefined => {
  if (!src) return undefined;
  // Only derive for relative/local paths we control. External URLs
  // (e.g. Supabase storage, Google reviews) keep their original src.
  if (/^https?:\/\//i.test(src)) return undefined;
  if (/\.(jpe?g|png)(\?.*)?$/i.test(src)) {
    return src.replace(/\.(jpe?g|png)(\?.*)?$/i, ".webp$2");
  }
  return undefined;
};

/**
 * <OptimizedImage /> — emits a <picture> with WebP + raster fallback,
 * explicit width/height (prevents CLS), async decoding, and lazy loading
 * by default. Pass `priority` for hero/above-the-fold images.
 */
export const OptimizedImage = ({
  src,
  webpSrc,
  alt,
  width,
  height,
  priority = false,
  className,
  ...rest
}: Props) => {
  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";
  const resolvedWebp = webpSrc ?? deriveWebp(src);

  const img = (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      className={className}
      {...rest}
    />
  );

  if (!resolvedWebp) return img;

  return (
    <picture>
      <source srcSet={resolvedWebp} type="image/webp" />
      {img}
    </picture>
  );
};
